import { is } from 'bpmn-js/lib/util/ModelUtil';
import { useService } from 'bpmn-js-properties-panel';
import { html } from 'htm/preact';
import { getOpenLinkModal } from '../utils/bridge';

/**
 * External Links group — shown for Process, Task, and SubProcess elements.
 * Stores links as a JSON array string in custom:externalLinks.
 */
export default function LinkGroup(element, injector) {
  const translate = injector.get('translate');

  const isApplicable =
    is(element, 'bpmn:Task') ||
    is(element, 'bpmn:SubProcess') ||
    is(element, 'bpmn:Process');

  if (!isApplicable) {
    return null;
  }

  return {
    id: 'externalLinks',
    label: translate('Externe Links'),
    entries: [
      {
        id: 'linkList',
        component: LinkListEntry
      },
      {
        id: 'addLink',
        component: AddLinkEntry
      }
    ]
  };
}

// --- Helper: read links from element ---
function readLinks(element) {
  const bo = element.businessObject;
  let raw = null;
  try { raw = bo.get('custom:externalLinks'); } catch { /* moddle fallback */ }
  if (!raw) raw = bo.$attrs?.['custom:externalLinks'] ?? null;
  if (!raw) return [];
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

// --- Helper: write links to element ---
function writeLinks(modeling, element, links) {
  const value = links.length > 0 ? JSON.stringify(links) : undefined;
  modeling.updateModdleProperties(element, element.businessObject, {
    'custom:externalLinks': value
  });
}

// --- Link List Entry ---
function LinkListEntry(props) {
  const { element } = props;
  const modeling = useService('modeling');
  const translate = useService('translate');
  const links = readLinks(element);

  const handleRemove = (index) => {
    const updated = links.filter((_, i) => i !== index);
    writeLinks(modeling, element, updated);
  };

  const handleOpen = (link) => {
    const fn = getOpenLinkModal();
    if (fn) {
      fn(link.url, link.title || link.url);
    } else {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    }
  };

  if (links.length === 0) {
    return html`
      <div class="bio-properties-panel-entry" style="padding: 6px 15px; color: #888; font-size: 12px;">
        ${translate('Keine externen Links vorhanden.')}
      </div>
    `;
  }

  return html`
    <div class="bio-properties-panel-entry external-links-list" style="padding: 6px 15px;">
      ${links.map((link, index) => html`
        <div key=${index} class="external-link-item" style="display: flex; align-items: center; gap: 6px; padding: 4px 0; border-bottom: 1px solid #f1f3f4;">
          <i class="bi bi-link-45deg" style="color: #0d6efd; flex-shrink: 0;"></i>
          <a
            href="#"
            onClick=${(e) => { e.preventDefault(); handleOpen(link); }}
            title=${link.url}
            style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; color: #0d6efd; text-decoration: none; cursor: pointer;"
          >
            ${link.title || link.url}
          </a>
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            style="padding: 1px 5px; font-size: 11px; line-height: 1; flex-shrink: 0;"
            onClick=${() => handleRemove(index)}
            title=${translate('Link entfernen')}
          >
            <i class="bi bi-trash"></i>
          </button>
        </div>
      `)}
    </div>
  `;
}

// --- Add Link Entry (no Preact hooks — uses uncontrolled inputs) ---
function AddLinkEntry(props) {
  const { element } = props;
  const modeling = useService('modeling');
  const translate = useService('translate');

  const handleAdd = (e) => {
    const container = e.target.closest('.add-link-entry');
    if (!container) return;
    const urlInput = container.querySelector('input[type="url"]');
    const titleInput = container.querySelector('input[type="text"]');
    const trimmedUrl = (urlInput?.value || '').trim();

    if (!trimmedUrl) return;

    if (!/^https?:\/\//i.test(trimmedUrl)) {
      alert(translate('Bitte gib eine gültige URL ein (http:// oder https://).'));
      return;
    }

    const titleVal = (titleInput?.value || '').trim();
    const links = readLinks(element);
    links.push({ url: trimmedUrl, title: titleVal || trimmedUrl });
    writeLinks(modeling, element, links);

    // Clear inputs after adding
    if (urlInput) urlInput.value = '';
    if (titleInput) titleInput.value = '';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd(e);
    }
  };

  return html`
    <div class="bio-properties-panel-entry add-link-entry" style="padding: 8px 15px;">
      <label class="bio-properties-panel-label" style="margin-bottom: 6px;">${translate('Link hinzufügen')}</label>
      <input
        type="url"
        class="bio-properties-panel-input"
        placeholder="https://example.com"
        onKeyDown=${handleKeyDown}
        style="width: 100%; padding: 5px 8px; border: 1px solid #ced4da; border-radius: 4px; font-size: 13px; margin-bottom: 5px;"
      />
      <input
        type="text"
        class="bio-properties-panel-input"
        placeholder=${translate('Titel (optional)')}
        onKeyDown=${handleKeyDown}
        style="width: 100%; padding: 5px 8px; border: 1px solid #ced4da; border-radius: 4px; font-size: 13px; margin-bottom: 6px;"
      />
      <button
        type="button"
        class="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
        onClick=${handleAdd}
        style="font-size: 12px;"
      >
        <i class="bi bi-plus-circle"></i> ${translate('Hinzufügen')}
      </button>
    </div>
  `;
}
